import { useQuery } from "@tanstack/react-query";

import {
  fetchJournalEntryWriteCapability,
  type JournalEntryWriteCapability,
} from "../../api/journalEntries";

export interface JournalEntryWriteCapabilityState {
  data: JournalEntryWriteCapability | null;
  isPending: boolean;
  isFetching: boolean;
  isExactSettled: boolean;
  error: Error | null;
}

/**
 * Project-scoped command availability with no cross-project or refetch flash.
 *
 * Capabilities are availability metadata, never a readability oracle. The
 * selected exact ArtifactVersion remains the only client-side readability
 * admission signal; the command rechecks it authoritatively.
 */
export function useJournalEntryWriteCapability(
  projectId: string,
  enabled = true,
): JournalEntryWriteCapabilityState {
  const shouldFetch = enabled && Boolean(projectId);
  const query = useQuery({
    queryKey: ["thread-journal-entry-write-capability-v1", projectId],
    enabled: shouldFetch,
    queryFn: ({ signal }) => fetchJournalEntryWriteCapability(projectId, { signal }),
    staleTime: 0,
    retry: false,
  });
  const error = query.error instanceof Error ? query.error : null;
  const isExactSettled = shouldFetch && query.isSuccess && !query.isFetching && error === null;
  return {
    data: isExactSettled ? query.data ?? null : null,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isExactSettled,
    error,
  };
}
